import {
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  InputAdornment,
  Chip,
  Alert,
} from "@mui/material";
import Button from "../../theme/components/Button.jsx";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";
import PropTypes from "prop-types";
import { useEffect, useState, useCallback, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { backendGet } from "../../services/dataService.js";
import { useSnackbar } from "notistack";
import { useClaimBookingActions } from "../../hooks/useClaimBookingActions.jsx";

const BookingModal = ({
  open,
  onClose,
  onSubmit = () => {},
  schaden_id,
  booking_id,
  isEdit = false,
  mode = "normal", // 'normal' or 'assignment'
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [schadenData, setSchadenData] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingAccount, setPendingAccount] = useState(null);
  const { updateClaimBooking, findById } = useClaimBookingActions();

  const [booking, setBooking] = useState();

  // Claim costs state
  const [bookableCosts, setBookableCosts] = useState([]);
  const [selectedCosts, setSelectedCosts] = useState({});
  const [isAutoCalc, setIsAutoCalc] = useState(true);

  // fetch Booking
  useEffect(() => {
    const fetchBooking = async () => {
      if (isEdit && booking_id && open) {
        await findById(booking_id, {
          setIsLoading,
          setResult: (data) => {
            setBooking(data);
            reset(data);
          },
          enqueueSnackbar,
        });
      }
    };
    fetchBooking();
  }, [isEdit, booking_id, open]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      bank_account_id: "",
      recipient: "",
      iban: "",
      bic: "",
      description: "",
      amount: "",
      status: "pending",
      new: true,
      id: `new-${uuidv4()}`,
      schaden_id: schaden_id || "",
    },
    reValidateMode: "onSubmit",
  });

  const { enqueueSnackbar } = useSnackbar();

  const applyBankAccount = useCallback(
    (bankAccount) => {
      if (bankAccount) {
        setValue("recipient", bankAccount.account_holder);
        setValue("iban", bankAccount.iban);
        setValue("bic", bankAccount.bic || "");
      } else {
        // Fields are cleared by reset() or manual user action
      }
    },
    [setValue]
  );

  useEffect(() => {
    if (!open || !schaden_id) {
      return;
    }

    let isMounted = true;

    const fetchAndSetup = async () => {
      setIsLoading(true);

      // Promisify the callback-based helper for use with async/await
      const backendGetPromise = (url, options) =>
        new Promise((resolve) => {
          backendGet(url, { ...options, setResult: resolve });
        });

      try {
        // Reset claim costs state
        setBookableCosts([]);
        setSelectedCosts({});
        setIsAutoCalc(true);

        if (isEdit) {
          // In edit mode, just populate the form and fetch accounts for the dropdown
          reset(booking);
          const [bankAccountsResponse, bookableCostsResponse] =
            await Promise.all([
              backendGetPromise(`data/schadens/${schaden_id}/bank-accounts`, {
                label: "Bankkonten",
                enqueueSnackbar,
              }),
              backendGetPromise(
                `data/schadens/${schaden_id}/costs/bookable?booking_id=${booking_id}&mode=${mode}`,
                { label: "Kostenpositionen", enqueueSnackbar }
              ),
            ]);
          if (!isMounted) return;
          setBankAccounts(bankAccountsResponse.data || []);
          const costsData = bookableCostsResponse.data || [];
          setBookableCosts(costsData);

          // Initialize selected costs from bookable costs response (uses fresh booked_in_current)
          const initialSelected = {};
          console.log(costsData);
          costsData.forEach((cost) => {
            if (cost.booked_in_current > 0) {
              initialSelected[cost.id] = {
                selected: true,
                booking_amount: cost.booked_in_current,
              };
            }
          });
          setSelectedCosts(initialSelected);
        } else {
          // For new bookings, fetch everything sequentially
          reset();
          setBankAccounts([]);
          setSchadenData(null);

          // 1. Fetch Bank Accounts, Schaden Data, and Bookable Costs
          const [bankAccountsResponse, schadenResponse, bookableCostsResponse] =
            await Promise.all([
              backendGetPromise(`data/schadens/${schaden_id}/bank-accounts`, {
                label: "Bankkonten",
                enqueueSnackbar,
              }),
              backendGetPromise(`data/schaden/${schaden_id}`, {
                enqueueSnackbar,
              }),
              backendGetPromise(`data/schadens/${schaden_id}/costs/bookable?mode=${mode}`, {
                label: "Kostenpositionen",
                enqueueSnackbar,
              }),
            ]);
          if (!isMounted) return;
          const bankAccountsData = bankAccountsResponse.data || [];
          setBankAccounts(bankAccountsData);
          setSchadenData(schadenResponse);
          setBookableCosts(bookableCostsResponse.data || []);

          // 2. Setup form with all data
          let defaultBankAccount = null;
          const payloadAccount = bankAccountsData.find((acc) => acc.id === 0);
          if (payloadAccount) {
            defaultBankAccount = payloadAccount;
          } else if (bankAccountsData.length > 0) {
            defaultBankAccount = bankAccountsData[0];
          }

          reset({
            bank_account_id: defaultBankAccount?.id ?? "",
            recipient: defaultBankAccount?.account_holder || "",
            iban: defaultBankAccount?.iban || "",
            bic: defaultBankAccount?.bic || "",
            description: `SP: ${schadenResponse?.schaden_nummer_makler}${schadenResponse?.schaden_nummer_vu ? ` - VU: ${schadenResponse.schaden_nummer_vu}` : ""}`,
            amount: "",
            status: "pending",
            new: true,
            id: `new-${uuidv4()}`,
            schaden_id,
          });

          if (defaultBankAccount) {
            applyBankAccount(defaultBankAccount);
          }
        }
      } catch (error) {
        console.error("Failed to fetch booking modal data:", error);
        if (isMounted) setIsLoading(false); // Ladevorgang stoppen
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchAndSetup();

    return () => {
      isMounted = false;
    };
  }, [open, schaden_id, isEdit, booking_id, mode]);

  const handleBankAccountChange = (event) => {
    // Use '==' to handle potential type difference (number vs string)
    const selectedAccount = bankAccounts.find(
      (account) => account.id == event.target.value
    );
    const currentValues = getValues();

    if (currentValues.recipient || currentValues.iban || currentValues.bic) {
      setPendingAccount(selectedAccount);
      setShowConfirmationModal(true);
    } else {
      applyBankAccount(selectedAccount);
      setValue("bank_account_id", selectedAccount?.id ?? "");
    }
  };

  const handleConfirmReplace = () => {
    applyBankAccount(pendingAccount);
    setShowConfirmationModal(false);
    setPendingAccount(null);
  };

  const handleCancelReplace = () => {
    setShowConfirmationModal(false);
    setPendingAccount(null);
  };

  // auto calculate total from selected costs
  const calculatedTotal = useMemo(() => {
    return Object.entries(selectedCosts).reduce((sum, [costId, costData]) => {
      if (costData.selected && costData.booking_amount) {
        return sum + parseFloat(costData.booking_amount || 0);
      }
      return sum;
    }, 0);
  }, [selectedCosts]);

  // Assignment mode is determined by prop, not derived from cost selection
  const isAssignmentBooking = mode === "assignment";

  // auto-update amount when costs change (if enabled)
  // Also clears the field when all costs are deselected (total == 0)
  useEffect(() => {
    if (!isAutoCalc) return;
    if (calculatedTotal > 0) {
      setValue("amount", calculatedTotal.toFixed(2).replace(".", ","));
    } else {
      const hasSelections = Object.values(selectedCosts).some((c) => c.selected);
      if (!hasSelections) {
        setValue("amount", "");
      }
    }
  }, [calculatedTotal, isAutoCalc, selectedCosts, setValue]);

  // handle cost selection toggle
  const handleCostToggle = (costId, cost) => {
    setSelectedCosts((prev) => {
      const isCurrentlySelected = prev[costId]?.selected;
      if (isCurrentlySelected) {
        // Deselect
        const { [costId]: _, ...rest } = prev;
        return rest;
      } else {
        // Select with full remaining amount
        return {
          ...prev,
          [costId]: {
            selected: true,
            booking_amount: cost.remaining_amount,
          },
        };
      }
    });
  };

  // Handle partial amount change
  const handleCostAmountChange = (costId, value, maxAmount) => {
    const numValue = parseFloat(value.replace(",", ".")) || 0;
    const clampedValue = Math.min(Math.max(0, numValue), maxAmount);

    setSelectedCosts((prev) => ({
      ...prev,
      [costId]: {
        ...prev[costId],
        booking_amount: clampedValue,
      },
    }));
  };

  // Handle manual amount field change (disables auto-calc)
  const handleManualAmountChange = (event, field) => {
    setIsAutoCalc(false);
    field.onChange(event);
  };

  // Reset to auto-calc mode
  const handleResetToAutoCalc = () => {
    setIsAutoCalc(true);
    if (calculatedTotal > 0) {
      setValue("amount", calculatedTotal.toFixed(2).replace(".", ","));
    }
  };

  // Format number for display
  const formatCurrency = (value) => {
    return parseFloat(value || 0).toLocaleString("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleManualInputChange = (event, field) => {
    // First, call the original onChange from react-hook-form to update the field's state
    field.onChange(event);

    // getValues() will have the updated state right after onChange
    const { recipient, iban, bic } = getValues();

    // Find if the current details match any existing bank account
    const matchingAccount = bankAccounts.find(
      (account) =>
        account.account_holder === recipient &&
        account.iban === iban &&
        (account.bic || "") === (bic || "") // Handle optional BIC
    );

    // Update the bank_account_id based on whether a match was found
    if (matchingAccount) {
      setValue("bank_account_id", matchingAccount.id);
    } else {
      setValue("bank_account_id", "");
    }
  };

  const handleFormSubmit = async (data) => {
    clearErrors();

    // Build claim_costs array from selected costs
    const claim_costs = Object.entries(selectedCosts)
      .filter(
        ([_, costData]) => costData.selected && costData.booking_amount > 0
      )
      .map(([costId, costData]) => ({
        id: parseInt(costId),
        booking_amount: costData.booking_amount,
      }));

    const submitData = {
      ...data,
      claim_costs,
      category: isAssignmentBooking ? "assignment" : (data.category ?? null),
    };

    // In edit mode, pass the previously linked costs so the delta can be reversed
    const previous_claim_costs = isEdit
      ? (booking?.claim_costs ?? []).map((c) => ({
          id: c.id,
          booking_amount: c.booking_amount,
        }))
      : [];

    try {
      await updateClaimBooking(submitData, {
        setIsLoading: setIsSaving,
        previous_claim_costs,
        onSuccess: (updatedBooking) => {
          const successMessage = isAssignmentBooking
            ? isEdit
              ? "Abtretungsbuchung erfolgreich aktualisiert."
              : "Abtretungsbuchung erfolgreich erstellt."
            : isEdit
              ? "Auszahlung erfolgreich aktualisiert."
              : "Auszahlung erfolgreich erstellt.";
          enqueueSnackbar(successMessage, { variant: "success" });
          onSubmit(submitData);
        },
      });
    } catch (error) {
      console.log("Error object in modal:", error);
      if (error.response && error.response.status === 422) {
        const validationErrors = error.response.data.errors;
        console.log("Backend validationErrors:", validationErrors);
        Object.entries(validationErrors).forEach(([field, messages]) => {
          setError(field, {
            type: "server",
            message: messages[0],
          });
        });
        // Add a timeout to log the errors object after setError
        setTimeout(() => {
          console.log("Form errors after setError:", errors); // DEBUG
        }, 100);
      } else {
        enqueueSnackbar("Ein unerwarteter Fehler ist aufgetreten.", {
          variant: "error",
        });
        console.error("Submission failed:", error);
      }
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth>
        <DialogTitle>
          {isEdit
            ? isAssignmentBooking
              ? "Statistische Buchung bearbeiten"
              : "Auszahlung bearbeiten"
            : isAssignmentBooking
              ? "Statistische Buchung erstellen"
              : "Neue Auszahlung erstellen"}
        </DialogTitle>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <DialogContent>
            {isLoading ? (
              <p>Laden...</p>
            ) : (
              <>
                {!isAssignmentBooking && (
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Bankkonto</InputLabel>
                    <Controller
                      name="bank_account_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          onChange={(event) => {
                            field.onChange(event);
                            handleBankAccountChange(event);
                          }}
                          error={!!errors.bank_account_id}
                        >
                          <MenuItem value="" disabled>
                            Bankverbindung auswählen
                          </MenuItem>
                          {bankAccounts.map((account) => (
                            <MenuItem key={account.id} value={account.id}>
                              <Box>
                                {account.name && (
                                  <Typography variant="body2">
                                    {account.name}
                                  </Typography>
                                )}
                                <Typography variant="body2" fontWeight="bold">
                                  {account.account_holder}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  {`IBAN: ${account.iban}${account.bic ? ` - BIC: ${account.bic}` : ""}`}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </FormControl>
                )}
                {!isAssignmentBooking && (
                  <Controller
                    name="recipient"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Empfänger"
                        fullWidth
                        margin="normal"
                        {...field}
                        onChange={(e) => handleManualInputChange(e, field)}
                        error={!!errors.recipient}
                        helperText={errors.recipient?.message}
                      />
                    )}
                  />
                )}
                {!isAssignmentBooking && (
                  <Controller
                    name="iban"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="IBAN"
                        fullWidth
                        margin="normal"
                        {...field}
                        onChange={(e) => handleManualInputChange(e, field)}
                        error={!!errors.iban}
                        helperText={errors.iban?.message}
                      />
                    )}
                  />
                )}
                {!isAssignmentBooking && (
                  <Controller
                    name="bic"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="BIC"
                        fullWidth
                        margin="normal"
                        {...field}
                        onChange={(e) => handleManualInputChange(e, field)}
                        error={!!errors.bic}
                        helperText={errors.bic?.message}
                      />
                    )}
                  />
                )}
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label={isAssignmentBooking ? "Buchungstext (optional)" : "Buchungstext"}
                      fullWidth
                      margin="normal"
                      {...field}
                    />
                  )}
                />

                {isAssignmentBooking && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Statistische Buchungen – wird nicht in den
                    Bankexport aufgenommen. Eine Gegenbuchung wird beim
                    Abschließen automatisch erstellt.
                  </Alert>
                )}

                {/* select claim costs */}
                {bookableCosts.length > 0 ? (
                  <Box sx={{ mt: 2, mb: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Kostenpositionen mit Abtretungserklärung auswählen
                    </Typography>
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{ maxHeight: 250 }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox" />
                            <TableCell>Kostenart</TableCell>
                            <TableCell align="right">Gesamt</TableCell>
                            <TableCell align="right">Verfügbar</TableCell>
                            <TableCell align="right">Buchungsbetrag</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {bookableCosts.map((cost) => {
                            const isSelected = selectedCosts[cost.id]?.selected;
                            const bookingAmount =
                              selectedCosts[cost.id]?.booking_amount || 0;
                            return (
                              <TableRow
                                key={cost.id}
                                hover
                                sx={{ cursor: "pointer" }}
                                onClick={() => handleCostToggle(cost.id, cost)}
                              >
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    checked={!!isSelected}
                                    onChange={() =>
                                      handleCostToggle(cost.id, cost)
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {cost.cost_label}
                                  </Typography>
                                  {cost.company_name && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {cost.company_name}
                                    </Typography>
                                  )}
                                  {cost.is_assigned && (
                                    <Chip
                                      label="Abgetreten"
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                    />
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {formatCurrency(cost.amount)} €
                                </TableCell>
                                <TableCell align="right">
                                  {formatCurrency(cost.remaining_amount)} €
                                </TableCell>
                                <TableCell
                                  align="right"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {isSelected ? (
                                    <TextField
                                      size="small"
                                      value={bookingAmount
                                        .toString()
                                        .replace(".", ",")}
                                      onChange={(e) =>
                                        handleCostAmountChange(
                                          cost.id,
                                          e.target.value,
                                          cost.remaining_amount
                                        )
                                      }
                                      sx={{ width: 100 }}
                                      slotProps={{
                                        input: {
                                          endAdornment: (
                                            <InputAdornment position="end">
                                              €
                                            </InputAdornment>
                                          ),
                                        },
                                        htmlInput: {
                                          ...{},

                                          ...{
                                            style: {
                                              textAlign: "right",
                                            },
                                          },
                                        },
                                      }}
                                    />
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      -
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {Object.keys(selectedCosts).length > 0 && (
                      <Box
                        sx={{
                          mt: 1,
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Typography variant="body2">
                          Summe ausgewählter Positionen:{" "}
                          <strong>{formatCurrency(calculatedTotal)} €</strong>
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {isAssignmentBooking
                      ? "Keine abgetretenen Kostenpositionen verfügbar. Sie können dennoch eine manuelle Abtretungsbuchung erstellen."
                      : "Keine offenen Kostenpositionen verfügbar. Entweder sind alle Positionen bereits gebucht oder es gibt keine Positionen mit einem Betrag größer 0 €."}
                  </Typography>
                )}

                <Controller
                  name="amount"
                  control={control}
                  rules={{
                    required: "Betrag ist erforderlich.",
                    validate: (value) =>
                      parseFloat(value.replace(",", ".")) > 0 ||
                      "Betrag muss positiv angegeben werden.",
                  }}
                  render={({ field }) => (
                    <TextField
                      label="Betrag"
                      fullWidth
                      margin="normal"
                      {...field}
                      onChange={(e) => handleManualAmountChange(e, field)}
                      error={!!errors.amount}
                      helperText={
                        errors.amount?.message ||
                        (bookableCosts.length > 0
                          ? isAutoCalc
                            ? "Automatisch berechnet aus ausgewählten Positionen"
                            : "Manuelle Eingabe (Auto-Berechnung deaktiviert)"
                          : "")
                      }
                      slotProps={{
                        input: {
                          endAdornment:
                            !isAutoCalc && bookableCosts.length > 0 ? (
                              <InputAdornment position="end">
                                €
                                <Tooltip title="Zurück zur automatischen Berechnung">
                                  <IconButton
                                    size="small"
                                    onClick={handleResetToAutoCalc}
                                    edge="end"
                                  >
                                    <RefreshIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ) : (
                              <InputAdornment position="end">€</InputAdornment>
                            ),
                        },
                        htmlInput: {
                          style: {
                            textAlign: "right",
                          },
                        },
                      }}
                    />
                  )}
                />
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ paddingX: 3, paddingBottom: 2 }}>
            <Button variant="tertiary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              type="submit"
              disabled={isSaving || isLoading}
              loading={isSaving}
            >
              {isEdit ? "Speichern" : "Erstellen"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      {/* Bestätigungsmodal */}
      <Dialog open={showConfirmationModal} onClose={handleCancelReplace}>
        <DialogTitle>Bestätigung</DialogTitle>
        <DialogContent>
          <Typography>
            Es sind bereits Daten in den Feldern vorhanden. Möchten Sie diese
            wirklich ersetzen?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="tertiary" onClick={handleCancelReplace}>
            Abbrechen
          </Button>
          <Button onClick={handleConfirmReplace}>Ersetzen</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

BookingModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  schaden_id: PropTypes.string,
  booking_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isEdit: PropTypes.bool,
  mode: PropTypes.oneOf(["normal", "assignment"]),
};

export default BookingModal;
